import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const AnimalFoodHabits: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/AnimalFoodHabits.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/AnimalFoodHabits.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Feeding Habits of Animals – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore the feeding habits of animals in Class 3 Science. Learn about herbivores, carnivores, omnivores, and how animals eat with fun examples at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🐾 Chapter 5: Animals: Food & Feeding Habits</h1>

                            <section>
                                <h2><span className="emoji">🍎</span>Why Do Animals Need Food?</h2>
                                <p>All animals need food to:</p>
                                <ul>
                                    <li>Get <strong>energy</strong> to move</li>
                                    <li><strong>Grow</strong> properly</li>
                                    <li><strong>Repair</strong> body parts</li>
                                    <li><strong>Stay healthy</strong></li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🍖</span>Types of Animals Based on Food Habits</h2>
                                <h3>1. Herbivores 🌿</h3>
                                <p>These animals eat only plants.</p>
                                <p><strong>Examples:</strong> Cow, Deer, Goat, Elephant</p>

                                <h3>2. Carnivores 🍗</h3>
                                <p>These animals eat meat (flesh of other animals).</p>
                                <p><strong>Examples:</strong> Lion, Tiger, Eagle</p>

                                <h3>3. Omnivores 🥩🥬</h3>
                                <p>These animals eat both plants and animals.</p>
                                <p><strong>Examples:</strong> Bear, Dog, Human, Crow</p>
                            </section>

                            <section>
                                <h2><span className="emoji">🦷</span>Animal Mouthparts and Feeding Styles</h2>
                                <table className="table table-bordered myTable">
                                    <thead>
                                        <tr>
                                            <th>Animal Type</th>
                                            <th>Mouthpart</th>
                                            <th>Function</th>
                                            <th>Example</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>Herbivores</td>
                                            <td>Flat teeth</td>
                                            <td>Chew plants and grass</td>
                                            <td>Cow, Horse</td>
                                        </tr>
                                        <tr>
                                            <td>Carnivores</td>
                                            <td>Sharp teeth & claws</td>
                                            <td>Tear meat</td>
                                            <td>Lion, Tiger</td>
                                        </tr>
                                        <tr>
                                            <td>Rodents</td>
                                            <td>Sharp front teeth</td>
                                            <td>Gnaw wood or nuts</td>
                                            <td>Rabbit, Rat</td>
                                        </tr>
                                        <tr>
                                            <td>Insects</td>
                                            <td>Long tube (proboscis)</td>
                                            <td>Suck nectar</td>
                                            <td>Butterfly</td>
                                        </tr>
                                        <tr>
                                            <td>Frogs/Lizards</td>
                                            <td>Sticky tongue</td>
                                            <td>Catch insects</td>
                                            <td>Frog, Chameleon</td>
                                        </tr>
                                        <tr>
                                            <td>Birds</td>
                                            <td>Beaks (no teeth)</td>
                                            <td>Peck and swallow</td>
                                            <td>Hen, Sparrow</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </section>

                            <section>
                                <h2><span className="emoji">🪺</span>How Do Animals Get Their Food?</h2>
                                <ul>
                                    <li>🦁 <strong>Hunting</strong> – Lions hunt for food</li>
                                    <li>🐄 <strong>Grazing</strong> – Cows graze on grass</li>
                                    <li>🐦 <strong>Pecking</strong> – Birds peck seeds</li>
                                    <li>🦋 <strong>Sucking</strong> – Butterflies suck nectar</li>
                                    <li>🐭 <strong>Gnawing</strong> – Rats gnaw using sharp teeth</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🌾</span>Interesting Eating Habits</h2>
                                <ul>
                                    <li>🐜 Ants carry food many times their size</li>
                                    <li>🐍 Snakes swallow their food whole</li>
                                    <li>🐝 Bees make honey from flower nectar</li>
                                    <li>🐄 Cows chew food twice – they are <strong>ruminants</strong></li>
                                </ul>
                            </section>

                            <section className="highlight-box">
                                <h2><span className="emoji">🧠</span>Summary</h2>
                                <ul>
                                    <li>Animals eat food to grow and stay active.</li>
                                    <li>There are <strong>herbivores, carnivores, and omnivores</strong>.</li>
                                    <li>They use different <strong>mouthparts</strong> to eat.</li>
                                    <li>Animals get food by <strong>hunting, grazing, sucking</strong>, and more.</li>
                                </ul>
                            </section>

                            <section className="fun-activities">
                                <h2><span className="emoji">📚</span>Fun Activities</h2>
                                <ul>
                                    <li>🖍️ Make a chart of 3 herbivores, 3 carnivores, and 3 omnivores</li>
                                    <li>🎨 Draw animals and their food habits</li>
                                    <li>🔍 Watch videos of animals eating and talk about it</li>
                                </ul>
                            </section>
                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🐾 Class 3 Science – Chapter 5: Animals: Food & Feeding Habits</h1>

                        <section>
                            <h2><span className="emoji">🍎</span>Why Do Animals Need Food?</h2>
                            <p>All animals need food to:</p>
                            <ul>
                                <li>Get <strong>energy</strong> to move</li>
                                <li><strong>Grow</strong> properly</li>
                                <li><strong>Repair</strong> body parts</li>
                                <li><strong>Stay healthy</strong></li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🍖</span>Types of Animals Based on Food Habits</h2>
                            <h3>1. Herbivores 🌿</h3>
                            <p>These animals eat only plants.</p>
                            <p><strong>Examples:</strong> Cow, Deer, Goat, Elephant</p>

                            <h3>2. Carnivores 🍗</h3>
                            <p>These animals eat meat (flesh of other animals).</p>
                            <p><strong>Examples:</strong> Lion, Tiger, Eagle</p>

                            <h3>3. Omnivores 🥩🥬</h3>
                            <p>These animals eat both plants and animals.</p>
                            <p><strong>Examples:</strong> Bear, Dog, Human, Crow</p>
                        </section>

                        <section>
                            <h2><span className="emoji">🦷</span>Animal Mouthparts and Feeding Styles</h2>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Animal Type</th>
                                        <th>Mouthpart</th>
                                        <th>Function</th>
                                        <th>Example</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Herbivores</td>
                                        <td>Flat teeth</td>
                                        <td>Chew plants and grass</td>
                                        <td>Cow, Horse</td>
                                    </tr>
                                    <tr>
                                        <td>Carnivores</td>
                                        <td>Sharp teeth & claws</td>
                                        <td>Tear meat</td>
                                        <td>Lion, Tiger</td>
                                    </tr>
                                    <tr>
                                        <td>Rodents</td>
                                        <td>Sharp front teeth</td>
                                        <td>Gnaw wood or nuts</td>
                                        <td>Rabbit, Rat</td>
                                    </tr>
                                    <tr>
                                        <td>Insects</td>
                                        <td>Long tube (proboscis)</td>
                                        <td>Suck nectar</td>
                                        <td>Butterfly</td>
                                    </tr>
                                    <tr>
                                        <td>Frogs/Lizards</td>
                                        <td>Sticky tongue</td>
                                        <td>Catch insects</td>
                                        <td>Frog, Chameleon</td>
                                    </tr>
                                    <tr>
                                        <td>Birds</td>
                                        <td>Beaks (no teeth)</td>
                                        <td>Peck and swallow</td>
                                        <td>Hen, Sparrow</td>
                                    </tr>
                                </tbody>
                            </table>
                        </section>

                        <section>
                            <h2><span className="emoji">🪺</span>How Do Animals Get Their Food?</h2>
                            <ul>
                                <li>🦁 <strong>Hunting</strong> – Lions hunt for food</li>
                                <li>🐄 <strong>Grazing</strong> – Cows graze on grass</li>
                                <li>🐦 <strong>Pecking</strong> – Birds peck seeds</li>
                                <li>🦋 <strong>Sucking</strong> – Butterflies suck nectar</li>
                                <li>🐭 <strong>Gnawing</strong> – Rats gnaw using sharp teeth</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🌾</span>Interesting Eating Habits</h2>
                            <ul>
                                <li>🐜 Ants carry food many times their size</li>
                                <li>🐍 Snakes swallow their food whole</li>
                                <li>🐝 Bees make honey from flower nectar</li>
                                <li>🐄 Cows chew food twice – they are <strong>ruminants</strong></li>
                            </ul>
                        </section>

                        <section className="highlight-box">
                            <h2><span className="emoji">🧠</span>Summary</h2>
                            <ul>
                                <li>Animals eat food to grow and stay active.</li>
                                <li>There are <strong>herbivores, carnivores, and omnivores</strong>.</li>
                                <li>They use different <strong>mouthparts</strong> to eat.</li>
                                <li>Animals get food by <strong>hunting, grazing, sucking</strong>, and more.</li>
                            </ul>
                        </section>

                        <section className="fun-activities">
                            <h2><span className="emoji">📚</span>Fun Activities</h2>
                            <ul>
                                <li>🖍️ Make a chart of 3 herbivores, 3 carnivores, and 3 omnivores</li>
                                <li>🎨 Draw animals and their food habits</li>
                                <li>🔍 Watch videos of animals eating and talk about it</li>
                            </ul>
                        </section>
                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default AnimalFoodHabits;
